import { AdocFileTable } from '@/components/features/Adoc/AdocFileTable';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Button } from '@/components/ui/shadcn/button';
import { EmptyState } from '@/components/ui/shadcn/empty-state';
import { Input } from '@/components/ui/shadcn/input';
import { useDebounce } from '@/hooks/use-debounce';
import { useServerFilters } from '@/hooks/use-server-filters';
import { useTranslations } from '@/hooks/useTranslations';
import IndexLayout from '@/layouts/index-layout';
import { cn } from '@/lib/utils';
import { type BreadcrumbItem, type PaginationMeta, type SharedData } from '@/types';
import type { AdocFileSummary, AdocFilterOption, AdocIndexFilters } from '@/types/adoc';
import { Head, router, usePage } from '@inertiajs/react';
import { FileCheck2, Loader2, Search, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { route } from 'ziggy-js';

interface AdocIndexProps {
    files: AdocFileSummary[];
    pagination: PaginationMeta;
    filters: AdocIndexFilters;
    users: AdocFilterOption[];
}

const ACCEPT = '.adoc';

export default function AdocIndex({ files, pagination, filters, users }: AdocIndexProps) {
    const { t } = useTranslations();
    const { auth } = usePage<SharedData>().props;
    const abilities = auth?.abilities ?? [];
    const canCreate = abilities.includes('adoc.create');
    const canDelete = abilities.includes('adoc.delete');

    const { applyFilters, loading } = useServerFilters(filters as unknown as Record<string, unknown>, '/adoc');

    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const [dragging, setDragging] = useState(false);

    const [searchValue, setSearchValue] = useState(filters.search ?? '');
    const debouncedSearch = useDebounce(searchValue, 800);

    useEffect(() => {
        if (debouncedSearch !== (filters.search ?? '')) {
            applyFilters({ search: debouncedSearch || undefined, page: 1 });
        }
    }, [debouncedSearch, filters.search, applyFilters]);

    const columnFilters: Record<string, string[]> = {
        uploaded_by: filters.uploaded_by ?? [],
    };
    const hasActiveFilters = (searchValue ?? '') !== '' || columnFilters.uploaded_by.length > 0;

    const breadcrumbs: BreadcrumbItem[] = [{ title: t('adoc.index.title', 'ADOC documents'), href: '/adoc' }];

    const handleFilterChange = (column: string, values: string[]): void => {
        applyFilters({ [column]: values.length > 0 ? values : undefined, page: 1 });
    };

    const handleSortChange = (column: string, direction: 'asc' | 'desc'): void => {
        applyFilters({ sort_by: column, sort_order: direction, page: 1 });
    };

    const clearFilters = (): void => {
        setSearchValue('');
        applyFilters({ search: undefined, uploaded_by: undefined, page: 1 });
    };

    const upload = (fileList: FileList | null): void => {
        const file = fileList?.[0];
        if (!file || uploading) {
            return;
        }

        router.post(
            route('adoc.store'),
            { file },
            {
                forceFormData: true,
                onStart: () => setUploading(true),
                onProgress: (event) => setUploadProgress(event?.percentage ?? null),
                onFinish: () => {
                    setUploading(false);
                    setUploadProgress(null);
                    if (inputRef.current) {
                        inputRef.current.value = '';
                    }
                },
            },
        );
    };

    const onDrop = (event: DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        setDragging(false);
        if (canCreate) {
            upload(event.dataTransfer.files);
        }
    };

    const refresh = (file: AdocFileSummary): void => {
        router.post(route('adoc.refresh', file.id), {}, { preserveScroll: true });
    };

    const destroy = (file: AdocFileSummary): void => {
        if (window.confirm(t('adoc.index.delete_confirm', 'Delete this container? This cannot be undone.'))) {
            router.delete(route('adoc.destroy', file.id), { preserveScroll: true });
        }
    };

    const uploadButton = canCreate ? (
        <Button onClick={() => inputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
            {uploading
                ? `${t('adoc.index.uploading', 'Uploading…')}${uploadProgress !== null ? ` ${uploadProgress}%` : ''}`
                : t('adoc.index.upload', 'Upload file')}
        </Button>
    ) : undefined;

    const showEmptyState = pagination.total === 0 && !hasActiveFilters;

    return (
        <>
            <Head title={t('adoc.index.title', 'ADOC documents')} />
            <IndexLayout
                breadcrumbs={breadcrumbs}
                title={t('adoc.index.title', 'ADOC documents')}
                subtitle={t('adoc.index.subtitle', 'Open signed ADOC e-document containers and read or download the documents inside.')}
                actions={uploadButton}
            >
                <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(event) => upload(event.target.files)} />

                {canCreate ? (
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => inputRef.current?.click()}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                inputRef.current?.click();
                            }
                        }}
                        onDragOver={(event) => {
                            event.preventDefault();
                            setDragging(true);
                        }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={onDrop}
                        className={cn(
                            'bg-card flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-8 text-center transition-colors',
                            dragging ? 'border-primary bg-accent' : 'hover:border-primary/60',
                        )}
                    >
                        <div className="bg-accent text-accent-foreground flex size-10 items-center justify-center rounded-lg [&>svg]:size-5">
                            <Upload />
                        </div>
                        <p className="text-sm font-medium">{t('adoc.index.dropzone.title', 'Drop an ADOC file here or click to browse')}</p>
                        <p className="text-muted-foreground text-xs">
                            {t('adoc.index.dropzone.hint', 'Signed .adoc e-document containers up to 50 MB')}
                        </p>
                    </div>
                ) : null}

                {showEmptyState ? (
                    <EmptyState
                        icon={<FileCheck2 />}
                        title={t('adoc.index.empty.title', 'No containers yet')}
                        description={t('adoc.index.empty.description', 'Upload a signed .adoc container to read the documents inside it.')}
                    />
                ) : (
                    <>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="relative w-full sm:max-w-xs">
                                <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                                <Input
                                    value={searchValue}
                                    onChange={(event) => setSearchValue(event.target.value)}
                                    placeholder={t('adoc.index.toolbar.search_placeholder', 'Search by title, file name or signer…')}
                                    className="pl-9"
                                />
                            </div>
                            <div className="text-muted-foreground flex items-center gap-3 text-sm">
                                <span>{t('adoc.index.toolbar.results', ':count containers').replace(':count', String(pagination.total))}</span>
                                {hasActiveFilters ? (
                                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                                        <X />
                                        {t('adoc.index.toolbar.clear_filters', 'Clear filters')}
                                    </Button>
                                ) : null}
                            </div>
                        </div>

                        <AdocFileTable
                            files={files}
                            users={users}
                            sortColumn={filters.sort_by}
                            sortDirection={filters.sort_order}
                            onSortChange={handleSortChange}
                            columnFilters={columnFilters}
                            onFilterChange={handleFilterChange}
                            canDelete={canDelete}
                            onRefresh={refresh}
                            onDelete={destroy}
                            emptyMessage={t('adoc.index.empty_filtered.title', 'No containers match')}
                        />

                        <PaginationControls
                            pagination={pagination}
                            onPageChange={(page) => applyFilters({ page })}
                            onPerPageChange={(perPage) => applyFilters({ per_page: perPage, page: 1 })}
                            loading={loading}
                        />
                    </>
                )}
            </IndexLayout>
        </>
    );
}
